import type { EvalParamValues } from "./evalParamSpecs";

// 평가는 seed 마다 시나리오 하나를 만든다. seed 는 평가 요청의 `seeds` 필드가 아니라
// `SIMULATION_SEED` 시나리오 파라미터에만 넣는다. 입력이 유효하지 않으면 사용자용 오류를 던진다.

// policy 하나에서 만드는 평가 run 의 최대 수.
export const MAX_SEEDS = 32;

// 시뮬레이션 seed 로 보는 환경 변수 키. 우선순위 순서다.
export const SEED_KEYS = ["SIMULATION_SEED", "SEED"] as const;

// 이미지에 seed 파라미터가 없으면 이 기본값으로 평가 하나를 만든다.
export const DEFAULT_SEED = 0;

export function isSeedParamKey(key: string): boolean {
  const upper = key.toUpperCase();
  return (SEED_KEYS as readonly string[]).includes(upper);
}

// 쉼표로 구분한 seed 를 음이 아닌 정수로 파싱한다. 유효하지 않거나 너무 많은 입력은 거절한다.
export function parseSeeds(raw: string): number[] {
  const seen = new Set<number>();
  const seeds: number[] = [];
  for (const chunk of raw.split(",")) {
    const trimmed = chunk.trim();
    if (trimmed === "") {
      continue;
    }
    if (!/^\d+$/.test(trimmed)) {
      throw new Error("SEED 값은 0 이상의 정수여야 합니다.");
    }
    const parsed = Number(trimmed);
    if (seen.has(parsed)) {
      continue;
    }
    seen.add(parsed);
    seeds.push(parsed);
  }
  if (seeds.length === 0) {
    throw new Error("SEED 값을 하나 이상 입력해 주세요.");
  }
  if (seeds.length > MAX_SEEDS) {
    throw new Error(`SEED 값은 최대 ${MAX_SEEDS}개까지 입력할 수 있습니다.`);
  }
  return seeds;
}

// 폼 값에서 seed key 를 찾는다. 이미지가 선언하지 않았으면 null 을 반환한다.
export function seedParamKey(values: EvalParamValues): string | null {
  return Object.keys(values).find((key) => isSeedParamKey(key)) ?? null;
}

// key 가 null 이면 seed 시나리오 파라미터를 추가하지 않고 평가 하나를 실행한다.
export type SeedPlan = {
  key: string | null;
  seeds: number[];
};

// seed key 와 기본값을 한곳에서 정한다. 유효하지 않으면 사용자용 검증 오류를 던진다.
export function resolveSeedPlan(values: EvalParamValues): SeedPlan {
  const key = seedParamKey(values);
  if (key === null) {
    return { key: null, seeds: [DEFAULT_SEED] };
  }
  return { key, seeds: parseSeeds(String(values[key] ?? "")) };
}

// 입력 중인 값이 유효하지 않으면 null 을 반환한다. 그래서 개수 힌트가 숨겨진 상태로 남는다.
export function safeSeedCount(raw: string): number | null {
  try {
    return parseSeeds(raw).length;
  } catch {
    return null;
  }
}
